/**
 * Rich Text Renderer for AI Florist Chat
 * ─────────────────────────────────────────────────────────────────
 * Lightweight markdown-to-HTML converter used by both the Shopify
 * storefront chat widget and the dashboard Sandbox (via copy).
 *
 * Supported syntax:
 *   **bold**           → <strong>
 *   *italic*           → <em>
 *   ## Heading         → <h3>
 *   ### Subheading     → <h4>
 *   - bullet           → <ul><li>
 *   1. numbered        → <ol><li>
 *   [text](url)        → <a>
 *   `code`             → <code>
 *   > blockquote       → <blockquote>
 *   ---                → <hr>
 *   \n\n               → paragraph break
 */

// eslint-disable-next-line no-unused-vars
function renderRichText(text) {
  if (!text) return '';

  // Escape HTML first
  var escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Split into blocks by double newline
  var blocks = escaped.split(/\n\n+/);
  var html = [];

  for (var b = 0; b < blocks.length; b++) {
    var block = blocks[b].trim();
    if (!block) continue;

    // Horizontal rule
    if (/^-{3,}$/.test(block)) {
      html.push('<hr class="afc-hr">');
      continue;
    }

    // Blockquote (lines starting with &gt;)
    if (/^&gt;\s/.test(block)) {
      var quoteLines = block.split('\n').map(function (l) {
        return l.replace(/^&gt;\s?/, '');
      });
      html.push('<blockquote class="afc-blockquote">' + formatInline(quoteLines.join('<br>')) + '</blockquote>');
      continue;
    }

    // Heading ## or ###
    var headingMatch = block.match(/^(#{2,3})\s+(.+)$/);
    if (headingMatch) {
      var level = headingMatch[1].length === 2 ? 'h3' : 'h4';
      html.push('<' + level + ' class="afc-heading">' + formatInline(headingMatch[2]) + '</' + level + '>');
      continue;
    }

    // Check if this block is a list
    var lines = block.split('\n');
    var isUnordered = lines.every(function (l) { return /^\s*[-•]\s/.test(l) || !l.trim(); });
    var isOrdered = lines.every(function (l) { return /^\s*\d+[.)]\s/.test(l) || !l.trim(); });

    if (isUnordered) {
      html.push('<ul class="afc-list">');
      for (var i = 0; i < lines.length; i++) {
        var item = lines[i].replace(/^\s*[-•]\s/, '').trim();
        if (item) html.push('<li>' + formatInline(item) + '</li>');
      }
      html.push('</ul>');
      continue;
    }

    if (isOrdered) {
      html.push('<ol class="afc-list afc-list--ordered">');
      for (var j = 0; j < lines.length; j++) {
        var ordItem = lines[j].replace(/^\s*\d+[.)]\s/, '').trim();
        if (ordItem) html.push('<li>' + formatInline(ordItem) + '</li>');
      }
      html.push('</ol>');
      continue;
    }

    // Regular paragraph — handle single newlines as <br>
    html.push('<p class="afc-para">' + formatInline(block.replace(/\n/g, '<br>')) + '</p>');
  }

  return html.join('');
}

function sanitizeHref(rawUrl) {
  var candidate = String(rawUrl || '').trim();
  if (!candidate) return '#';

  var compact = candidate.replace(/\s+/g, '').toLowerCase();
  if (
    compact.indexOf('javascript:') === 0 ||
    compact.indexOf('data:') === 0 ||
    compact.indexOf('vbscript:') === 0
  ) {
    return '#';
  }

  if (/^(https?:|mailto:|tel:|\/|#)/i.test(candidate)) {
    return candidate;
  }

  return '#';
}

/**
 * Format inline elements: bold, italic, code, links.
 */
function formatInline(text) {
  return text
    // Bold: **text**
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* (but not inside bold)
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
    // Inline code: `code`
    .replace(/`([^`]+)`/g, '<code class="afc-code">$1</code>')
    // Links: [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, function (_match, label, url) {
      return '<a href="' + sanitizeHref(url) + '" class="afc-link" target="_blank" rel="noopener noreferrer">' + label + '</a>';
    });
}
